import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import BenevitaLogo from '../../components/BenevitaLogo';

const fmtErr = (d, fallback) => {
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg ? `${(e.loc || []).slice(-1)[0] || 'field'}: ${e.msg}` : JSON.stringify(e)).join('; ');
  if (typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
  return String(d);
};

// ── Inline SVG icons (no external deps) ──────────────────────────────────────
const IconUser = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconLock = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconEye = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const IconEyeOff = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);
const IconArrow = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
const IconShield = (props) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);
const IconSpark = (props) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2l1.6 5.5L19 9l-5.4 1.5L12 16l-1.6-5.5L5 9l5.4-1.5z" />
  </svg>
);

const FEATURES = [
  { label: 'Party Model'    },
  { label: 'Nurse Dispatch' },
  { label: 'Care Plans'     },
  { label: 'Visit Reports'  },
  { label: 'Billing'        },
  { label: 'Audit Logs'     },
];
const COMPLIANCE = ['FHIR R4', 'HIPAA', 'ICD-10', 'Kenya DPA'];

// ── Field with leading icon, optional trailing button ─────────────────────────
// TOP-LEVEL on purpose: defining this inside Login() gave it a new component
// identity on every render, so React remounted the <input> per keystroke and
// focus was lost after the first character (the "can't type" bug). It owns
// its focus styling locally instead of reading parent state.
function Field({ id, label, type, value, onChange, placeholder, leading, trailing, autoComplete }) {
  const [isFocused, setIsFocused] = useState(false);
  return (
    <div style={{ marginBottom: 18 }}>
      <label htmlFor={id} style={{
        display: 'block', fontSize: 11, fontWeight: 700, color: '#1e3348',
        marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.6px',
      }}>
        {label}
      </label>
      <div style={{
        position: 'relative', display: 'flex', alignItems: 'center',
        background: '#fff',
        border: `1px solid ${isFocused ? '#7b1e3c' : '#ece3e7'}`,
        borderRadius: 10,
        boxShadow: isFocused
          ? '0 0 0 4px rgba(123,30,60,0.12), 0 1px 2px rgba(13,27,46,0.04)'
          : '0 1px 2px rgba(13,27,46,0.04)',
        transition: 'border-color .15s, box-shadow .15s',
      }}>
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          paddingLeft: 14, color: isFocused ? '#7b1e3c' : '#90a4ae',
          transition: 'color .15s',
        }}>
          {leading}
        </span>
        <input
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required
          onFocus={() => setIsFocused(true)}
          onBlur={()  => setIsFocused(false)}
          style={{
            flex: 1, padding: '12px 14px 12px 10px',
            border: 'none', outline: 'none', background: 'transparent',
            fontSize: 14, color: '#2b1b22', fontFamily: 'Inter, sans-serif',
          }}
        />
        {trailing}
      </div>
    </div>
  );
}

export default function Login() {
  const [form, setForm]           = useState({ email: '', password: '' });
  const [error, setError]         = useState('');
  const [info, setInfo]           = useState('');
  const [loading, setLoading]     = useState(false);
  const [showPwd, setShowPwd]     = useState(false);
  // 'login' | 'change' | 'forgot' | 'reset' — 'change' is the forced
  // first-login password rotation (account was issued a temporary password
  // by an admin); 'forgot'/'reset' are the self-service "I forgot it" flow.
  const [stage, setStage]         = useState('login');
  const [newPwd, setNewPwd]       = useState({ password: '', confirm: '' });
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode]   = useState('');

  const { login } = useAuth();
  const navigate  = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await login(form.email, form.password);
      if (data?.must_change_password) {
        setStage('change');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail, 'Invalid credentials. Please check your email and password.'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authAPI.forgotPassword(resetEmail);
      setInfo("If an account matches, we've sent a password reset code to its email.");
      setStage('reset');
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail, 'Could not send the reset code.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (newPwd.password !== newPwd.confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError('');
    try {
      await authAPI.resetPassword(resetEmail, resetCode, newPwd.password);
      setInfo('Password updated. Sign in with your new password.');
      setStage('login');
      setForm({ email: resetEmail, password: '' });
      setNewPwd({ password: '', confirm: '' });
      setResetCode('');
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail, 'Could not reset the password. The code may be invalid or expired.'));
    } finally {
      setLoading(false);
    }
  };

  const handleForcedChange = async (e) => {
    e.preventDefault();
    if (newPwd.password !== newPwd.confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError('');
    try {
      await authAPI.changePassword({ current_password: form.password, new_password: newPwd.password });
      // Changing the password revokes every session — sign back in with the new one.
      await login(form.email, newPwd.password);
      navigate('/dashboard');
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail, 'Could not update the password.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', fontFamily: 'Inter, sans-serif',
      background: '#f4f6fa',
    }}>
      {/* ════════════════════════════════════════════════════════════════════
          LEFT — branding panel
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="login-left" style={{
        flex: '0 0 460px', position: 'relative', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        background: 'linear-gradient(160deg, #4a1228 0%, #7b1e3c 45%, #7b1e3c 100%)',
        padding: '44px 44px', justifyContent: 'space-between',
        color: '#fff',
      }}>
        {/* Decorative orbs */}
        <div aria-hidden style={{
          position: 'absolute', top: -120, right: -120,
          width: 380, height: 380, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(217,140,179,0.18) 0%, rgba(217,140,179,0) 70%)',
          pointerEvents: 'none',
        }} />
        <div aria-hidden style={{
          position: 'absolute', bottom: -160, left: -140,
          width: 420, height: 420, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(123,30,60,0.22) 0%, rgba(123,30,60,0) 70%)',
          pointerEvents: 'none',
        }} />
        {/* Subtle dot grid */}
        <div aria-hidden style={{
          position: 'absolute', inset: 0, opacity: 0.5,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.6), rgba(0,0,0,0))',
          WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0.6), rgba(0,0,0,0))',
          pointerEvents: 'none',
        }} />

        {/* Logo */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14 }}>
          <BenevitaLogo size={44} variant="mark" onDark />
          <div>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: 24, fontWeight: 400, color: '#fff', letterSpacing: '-0.4px' }}>benevita</div>
            <div style={{ fontSize: 10, color: '#e3c77e', letterSpacing: '1.4px', textTransform: 'uppercase', marginTop: 2, fontWeight: 600 }}>
              Home Health Care
            </div>
          </div>
        </div>

        {/* Hero copy */}
        <div style={{ position: 'relative' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 12px', borderRadius: 999,
            background: 'rgba(217,140,179,0.12)',
            border: '1px solid rgba(217,140,179,0.25)',
            fontSize: 11, fontWeight: 600, color: '#e6a8c6',
            marginBottom: 18, letterSpacing: '0.2px',
          }}>
            <IconSpark style={{ color: '#e6a8c6' }} />
            Trusted by clinical teams
          </div>
          <h2 style={{
            fontSize: 32, fontWeight: 800, color: '#ffffff',
            margin: '0 0 14px', lineHeight: 1.18, letterSpacing: '-0.6px',
          }}>
            Coordinating care,<br />
            <span style={{
              background: 'linear-gradient(90deg,#c39a4e,#e6a8c6)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              one home at a time.
            </span>
          </h2>
          <p style={{ fontSize: 14, color: '#cda7bb', lineHeight: 1.65, margin: 0, maxWidth: 340 }}>
            Manage patients, nurses, referrals, visits, and billing —
            all in one secure clinical platform.
          </p>

          {/* Feature chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 26 }}>
            {FEATURES.map(f => (
              <span key={f.label} style={{
                fontSize: 11, fontWeight: 600, color: '#f0dcc2',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 8, padding: '6px 11px',
                border: '1px solid rgba(255,255,255,0.10)',
                backdropFilter: 'blur(8px)',
              }}>{f.label}</span>
            ))}
          </div>
        </div>

        {/* Compliance footer */}
        <div style={{ position: 'relative' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
            color: '#c39a4e', fontSize: 10, fontWeight: 700,
            letterSpacing: '1.2px', textTransform: 'uppercase',
          }}>
            <IconShield style={{ color: '#c39a4e' }} />
            Compliance
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {COMPLIANCE.map(c => (
              <span key={c} style={{
                fontSize: 11, color: '#e3c77e', fontWeight: 600,
                padding: '4px 10px', borderRadius: 6,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}>{c}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          RIGHT — form card
      ═══════════════════════════════════════════════════════════════════════ */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 24px', position: 'relative',
      }}>
        {/* Subtle backdrop accents */}
        <div aria-hidden style={{
          position: 'absolute', top: 60, right: 60,
          width: 220, height: 220, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(123,30,60,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{
          width: '100%', maxWidth: 420, position: 'relative',
          background: '#ffffff', borderRadius: 16,
          padding: '40px 38px',
          border: '1px solid #ece3e7',
          boxShadow: '0 1px 2px rgba(13,27,46,0.04), 0 12px 32px -8px rgba(13,27,46,0.08)',
        }}>
          {/* Heading */}
          <div style={{ marginBottom: 26 }}>
            <h1 style={{
              fontSize: 24, fontWeight: 800, color: '#2b1b22',
              margin: '0 0 6px', letterSpacing: '-0.4px',
            }}>
              {stage === 'change' ? 'Set a new password'
                : stage === 'forgot' ? 'Reset your password'
                : stage === 'reset' ? 'Enter the reset code'
                : 'Welcome back'}
            </h1>
            <p style={{ color: '#8b7681', fontSize: 13.5, margin: 0, lineHeight: 1.5 }}>
              {stage === 'change'
                ? 'Your password was issued by an administrator and must be changed before you continue. Use at least 10 characters with upper & lower case, a digit and a symbol.'
                : stage === 'forgot'
                ? "Enter the email on your account and we'll send a reset code to it."
                : stage === 'reset'
                ? `We've sent a code to ${resetEmail || 'your email'}. Enter it below with a new password.`
                : 'Sign in to your Benevita workspace to continue.'}
            </p>
          </div>

          {/* Info banner */}
          {info && !error && (
            <div role="status" style={{
              background: 'linear-gradient(180deg,#f3fbf6,#e9f7ee)',
              color: '#1e6b3e', padding: '11px 14px',
              borderRadius: 10, marginBottom: 18, fontSize: 13,
              border: '1px solid #bfe6cd',
              display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <span style={{ lineHeight: 1.5 }}>{info}</span>
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div role="alert" style={{
              background: 'linear-gradient(180deg,#fff5f5,#fdecea)',
              color: '#b71c1c', padding: '11px 14px',
              borderRadius: 10, marginBottom: 18, fontSize: 13,
              border: '1px solid #f5c2c0',
              display: 'flex', gap: 10, alignItems: 'flex-start',
              boxShadow: '0 1px 2px rgba(183,28,28,0.06)',
            }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 20, height: 20, borderRadius: '50%',
                background: '#b71c1c', color: '#fff', fontSize: 12, fontWeight: 800,
                flexShrink: 0, marginTop: 1,
              }}>!</span>
              <span style={{ lineHeight: 1.5 }}>{error}</span>
            </div>
          )}

          {stage === 'forgot' ? (
          <form onSubmit={handleForgotSubmit} noValidate>
            <Field
              id="forgot-email"
              label="Email"
              type="email"
              value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
              placeholder="Enter your account email"
              autoComplete="username"
              leading={<IconUser />}
            />
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px 16px',
                background: loading ? '#90a4ae' : 'linear-gradient(180deg,#94335a,#7b1e3c)',
                color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif', letterSpacing: '0.2px', marginTop: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {loading ? 'Sending…' : 'Send reset code'}
            </button>
            <button
              type="button"
              onClick={() => { setStage('login'); setError(''); setInfo(''); }}
              style={{
                width: '100%', padding: '10px 16px', marginTop: 10,
                background: 'transparent', border: 'none', color: '#7b1e3c',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              }}
            >
              Back to sign in
            </button>
          </form>
          ) : stage === 'reset' ? (
          <form onSubmit={handleResetSubmit} noValidate>
            <Field
              id="reset-code"
              label="Reset code"
              type="text"
              value={resetCode}
              onChange={e => setResetCode(e.target.value)}
              placeholder="6-digit code from your email"
              autoComplete="one-time-code"
              leading={<IconShield />}
            />
            <Field
              id="reset-new-password"
              label="New password"
              type={showPwd ? 'text' : 'password'}
              value={newPwd.password}
              onChange={e => setNewPwd({ ...newPwd, password: e.target.value })}
              placeholder="Choose a strong password"
              autoComplete="new-password"
              leading={<IconLock />}
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPwd(s => !s)}
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: '0 14px 0 6px', color: '#90a4ae',
                  }}
                >
                  {showPwd ? <IconEyeOff /> : <IconEye />}
                </button>
              }
            />
            <Field
              id="reset-confirm-password"
              label="Confirm new password"
              type={showPwd ? 'text' : 'password'}
              value={newPwd.confirm}
              onChange={e => setNewPwd({ ...newPwd, confirm: e.target.value })}
              placeholder="Repeat the new password"
              autoComplete="new-password"
              leading={<IconLock />}
            />
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px 16px',
                background: loading ? '#90a4ae' : 'linear-gradient(180deg,#94335a,#7b1e3c)',
                color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif', letterSpacing: '0.2px', marginTop: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {loading ? 'Resetting…' : 'Reset password'}
            </button>
            <button
              type="button"
              onClick={() => { setStage('forgot'); setError(''); setInfo(''); }}
              style={{
                width: '100%', padding: '10px 16px', marginTop: 10,
                background: 'transparent', border: 'none', color: '#7b1e3c',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              }}
            >
              Didn't get a code? Try again
            </button>
          </form>
          ) : stage === 'change' ? (
          <form onSubmit={handleForcedChange} noValidate>
            <Field
              id="new-password"
              label="New password"
              type={showPwd ? 'text' : 'password'}
              value={newPwd.password}
              onChange={e => setNewPwd({ ...newPwd, password: e.target.value })}
              placeholder="Choose a strong password"
              autoComplete="new-password"
              leading={<IconLock />}
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPwd(s => !s)}
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: '0 14px 0 6px', color: '#90a4ae',
                  }}
                >
                  {showPwd ? <IconEyeOff /> : <IconEye />}
                </button>
              }
            />
            <Field
              id="confirm-password"
              label="Confirm new password"
              type={showPwd ? 'text' : 'password'}
              value={newPwd.confirm}
              onChange={e => setNewPwd({ ...newPwd, confirm: e.target.value })}
              placeholder="Repeat the new password"
              autoComplete="new-password"
              leading={<IconLock />}
            />
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px 16px',
                background: loading ? '#90a4ae' : 'linear-gradient(180deg,#94335a,#7b1e3c)',
                color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif', letterSpacing: '0.2px', marginTop: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {loading ? 'Updating…' : 'Update password & continue'}
            </button>
          </form>
          ) : (
          <form onSubmit={handleSubmit} noValidate>
            <Field
              id="email"
              label="Email"
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="Enter your email"
              autoComplete="username"
              leading={<IconUser />}
            />

            <Field
              id="password"
              label="Password"
              type={showPwd ? 'text' : 'password'}
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              placeholder="Enter your password"
              autoComplete="current-password"
              leading={<IconLock />}
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPwd(s => !s)}
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: '0 14px 0 6px', color: '#90a4ae',
                  }}
                >
                  {showPwd ? <IconEyeOff /> : <IconEye />}
                </button>
              }
            />

            <div style={{ textAlign: 'right', marginTop: -10, marginBottom: 18 }}>
              <button
                type="button"
                onClick={() => { setResetEmail(form.email); setStage('forgot'); setError(''); setInfo(''); }}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: '#7b1e3c', fontSize: 12.5, fontWeight: 700,
                  fontFamily: 'Inter, sans-serif', padding: 0,
                }}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px 16px',
                background: loading
                  ? '#90a4ae'
                  : 'linear-gradient(180deg,#94335a,#7b1e3c)',
                color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif',
                transition: 'transform .08s, box-shadow .15s, filter .15s',
                letterSpacing: '0.2px', marginTop: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: loading
                  ? 'none'
                  : '0 6px 16px -4px rgba(123,30,60,0.45), inset 0 1px 0 rgba(255,255,255,0.18)',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.filter = 'brightness(1.06)'; }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.filter = 'none'; }}
              onMouseDown={e => { if (!loading) e.currentTarget.style.transform = 'translateY(1px)'; }}
              onMouseUp={e => { if (!loading) e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {loading ? (
                <>
                  <span className="hhms-spinner" style={{
                    width: 14, height: 14, borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: '#fff',
                    display: 'inline-block',
                  }} />
                  Signing in…
                </>
              ) : (
                <>
                  Sign In
                  <IconArrow />
                </>
              )}
            </button>
          </form>
          )}

          {/* Patient self-registration */}
          {stage === 'login' && (
            <p style={{ textAlign: 'center', marginTop: 18, marginBottom: 0, fontSize: 12.5, color: '#8b7681' }}>
              New patient?{' '}
              <Link to="/signup" style={{ color: '#7b1e3c', fontWeight: 700, textDecoration: 'none' }}>
                Create a patient account
              </Link>
            </p>
          )}

          {/* Footer */}
          <p style={{
            textAlign: 'center', marginTop: 14, marginBottom: 0,
            fontSize: 11, color: '#90a4ae', lineHeight: 1.6,
          }}>
            Protected workspace · Sessions are audited for HIPAA compliance.
          </p>
        </div>
      </div>

      {/* Animations + responsive */}
      <style>{`
        @keyframes hhms-spin { to { transform: rotate(360deg); } }
        .hhms-spinner { animation: hhms-spin 0.7s linear infinite; }

        @media (max-width: 820px) {
          .login-left { flex: 0 0 360px !important; padding: 32px 28px !important; }
        }
        @media (max-width: 680px) {
          .login-left { display: none !important; }
        }
      `}</style>
    </div>
  );
}
